import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchAdminMe, fetchGuideFeedbackStats, guideFeedbackExportUrl } from "@/lib/api";
import { Star, Loader2, ArrowLeft, Download, MessageSquare, ThumbsUp, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const LANG_LABELS = { fr: "Français", wo: "Wolof", di: "Dioula", bm: "Bambara" };

export default function AdminGuideFeedbackPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    fetchGuideFeedbackStats().then(setData).catch(() => toast.error("Erreur de chargement"));
  }, []);

  useEffect(() => {
    fetchAdminMe().then(load).catch(() => navigate("/", { replace: true }));
  }, [navigate, load]);

  if (!data) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-20 text-center">
        <Loader2 className="animate-spin mx-auto text-orange-500" />
      </main>
    );
  }

  const maxCount = Math.max(1, ...Object.values(data.distribution));

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-guide-feedback-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-orange-600 font-semibold mb-4" data-testid="back-to-admin">
        <ArrowLeft size={14} /> Tableau de bord
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
          <BarChart3 size={32} className="text-orange-500" /> Feedback guide biométrique
        </h1>
        <a
          href={guideFeedbackExportUrl}
          className="btn-secondary inline-flex items-center gap-2"
          data-testid="feedback-export-btn"
        >
          <Download size={15} /> Exporter (CSV)
        </a>
      </div>
      <p className="text-[var(--ink-soft)] mt-2">
        Avis laissés par les utilisateurs sur le guide biométrique en 4 étapes.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <StatCard icon={Star} label="Note moyenne" value={data.total ? `${data.average}/5` : "—"} testid="stat-average" />
        <StatCard icon={ThumbsUp} label="Avis positifs (4-5⭐)" value={`${data.positive_pct}%`} testid="stat-positive" />
        <StatCard icon={MessageSquare} label="Total des avis" value={data.total} testid="stat-total" />
        <StatCard
          icon={BarChart3}
          label="Langue la plus utilisée"
          value={
            Object.keys(data.languages).length
              ? LANG_LABELS[Object.entries(data.languages).sort((a, b) => b[1] - a[1])[0][0]] ||
                Object.entries(data.languages).sort((a, b) => b[1] - a[1])[0][0]
              : "—"
          }
          testid="stat-top-lang"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <section className="glass-card p-6" data-testid="feedback-distribution">
          <h2 className="font-bold text-lg mb-4">Répartition des notes</h2>
          {[5, 4, 3, 2, 1].map((n) => (
            <div key={n} className="flex items-center gap-3 mb-2">
              <span className="text-xs font-bold w-8 shrink-0">{n} ⭐</span>
              <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${n >= 4 ? "bg-emerald-500" : n === 3 ? "bg-amber-400" : "bg-red-400"}`}
                  style={{ width: `${(data.distribution[String(n)] / maxCount) * 100}%` }}
                />
              </div>
              <span className="text-xs text-[var(--ink-soft)] w-8 text-right shrink-0">{data.distribution[String(n)]}</span>
            </div>
          ))}
        </section>

        <section className="glass-card p-6" data-testid="feedback-comments">
          <h2 className="font-bold text-lg mb-4">Derniers commentaires</h2>
          {data.recent_comments.length === 0 ? (
            <p className="text-sm text-[var(--ink-soft)]">Aucun commentaire pour le moment.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {data.recent_comments.map((c) => (
                <div key={c.id} className="border-b border-[var(--line)] pb-2">
                  <div className="flex items-center gap-2 text-xs text-[var(--ink-soft)]">
                    <span className="font-bold text-amber-600">{"⭐".repeat(c.rating)}</span>
                    <span>{LANG_LABELS[c.language] || c.language}</span>
                    <span>{(c.created_at || "").slice(0, 10)}</span>
                  </div>
                  <p className="text-sm mt-1">{c.comment}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[var(--ink-soft)] mb-1">
        <Icon size={14} className="text-orange-500" /> {label}
      </div>
      <div className="text-2xl font-extrabold">{value}</div>
    </div>
  );
}
